/**
 * /chat — embedded Bynaus assistant with streaming + write tools.
 *
 * Streaming flow:
 *   1. User submits a message
 *   2. POST /api/chat/stream with the conversation; server returns an SSE
 *      response. We read it via fetch + ReadableStream + a tiny SSE parser
 *      (we can't use EventSource because EventSource is GET-only).
 *   3. Each event mutates the in-progress assistant message:
 *        text_delta       → append to the message text
 *        tool_use_start   → push a placeholder tool_call entry (loading)
 *        tool_use_result  → fill in the result/error on that placeholder
 *        done / error     → finalize
 *   4. The user sees Claude's text appear word-by-word AND tool calls
 *      light up live ("calling get_account_balance ⏳ ✓").
 *
 * Conversation history:
 *   On first message, a new conversation is created via POST /api/chat/conversations.
 *   All subsequent messages carry the conversation_id, which the server uses
 *   to persist the exchange. The left sidebar lists saved conversations;
 *   clicking one loads its stored messages and resumes.
 *
 * Memory:
 *   The server injects per-user memory into every system prompt.
 *   The memory panel (right side) lets users view / add / delete memories.
 *   The AI can also save memories itself via the save_memory tool.
 */
import { appendStreamError, rememberStreamErrorRef, streamHttpError, withSupportRef } from "@/lib/supportRef";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";

import { getToken } from "@/lib/api";
import {
  chatConversationsQueryOptions,
  chatMessagesQueryOptions,
  chatKeys,
  chatToolsQueryOptions,
} from "@/lib/queries";
import type {
  ChatConversation,
  ChatConversationMessage,
  ChatMessage,
  ChatToolCallTrace,
} from "@/lib/types";
import { applyAgentNavigation } from "@/lib/agentNavigation";
import { ChatSidebar } from "@/components/ChatSidebar";
import { MemoryPanel } from "@/components/MemoryPanel";
import { AgentWriteConsent } from "@/components/AgentWriteConsent";

export const Route = createFileRoute("/_auth/chat")({
  component: ChatRoute,
});

// The Bynaus assistant can call write tools (post journals, AP/AR invoices,
// etc.), so it's a write-capable agent surface and must clear the one-time
// agent-write consent gate before the user can interact with it.
function ChatRoute() {
  return (
    <AgentWriteConsent agentName="the Bynaus assistant">
      <ChatPage />
    </AgentWriteConsent>
  );
}

interface DisplayMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  tool_calls: ChatToolCallTrace[];
  /** True while the SSE stream for this message is still arriving. */
  streaming?: boolean;
  /** True if the stream errored out before completing. */
  errored?: boolean;
  /** Model id that produced this turn (e.g. "claude-sonnet-4-5"). */
  model?: string;
  /** Local blob URL for displaying an attached image thumbnail. */
  imageSrc?: string;
}

const SUGGESTED_PROMPTS = [
  "What's our cash balance?",
  "Show me the most recent posted journals.",
  "Which AP bills are unpaid?",
  "How much do customers owe us right now?",
  "What did we spend on labor in the last month?",
  "List the open fiscal periods.",
];

function ChatPage() {
  const qc = useQueryClient();
  const router = useRouter();
  const toolsQuery = useQuery(chatToolsQueryOptions());

  // ── Conversation state ───────────────────────────────────────
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);

  // ── Image attachment state ───────────────────────────────────
  const [pendingImage, setPendingImage] = useState<{ url: string; localSrc: string; filename: string } | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom on every state change.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // ── Create a new conversation ────────────────────────────────
  const createConvMutation = useMutation({
    mutationFn: (title: string) =>
      api<ChatConversation>("/api/chat/conversations", {
        method: "POST",
        body: JSON.stringify({ title }),
      }),
    onSuccess: (conv) => {
      // Add to sidebar list at the top
      qc.setQueryData<ChatConversation[]>(chatKeys.conversations(), (prev) =>
        prev ? [conv, ...prev] : [conv],
      );
    },
  });

  // ── Load an existing conversation ────────────────────────────
  const loadConversation = useCallback(
    async (conv: ChatConversation) => {
      setActiveConvId(conv.id);
      // Fetch stored messages and hydrate display state.
      // We use the query cache so refetches are cheap.
      const msgs = await qc.fetchQuery(chatMessagesQueryOptions(conv.id));
      setMessages(
        msgs.map((m: ChatConversationMessage) => ({
          id: m.id,
          role: m.role as "user" | "assistant",
          text: m.content,
          tool_calls: m.tool_calls ?? [],
          model: m.model ?? undefined,
        })),
      );
    },
    [qc],
  );

  // ── New chat (clear state) ───────────────────────────────────
  const startNewChat = () => {
    setActiveConvId(null);
    setMessages([]);
    setInput("");
  };

  // ── Image upload ─────────────────────────────────────────────
  const uploadImage = async (file: File) => {
    setImageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const r = await fetch("/api/chat/upload-image", {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken() ?? ""}` },
        body: fd,
      });
      if (!r.ok) throw new Error(`Upload failed: ${r.status}`);
      const result = await r.json() as { image_url: string; filename: string };
      const localSrc = URL.createObjectURL(file);
      setPendingImage({ url: result.image_url, localSrc, filename: result.filename });
    } catch (e) {
      console.error("Image upload failed", e);
    } finally {
      setImageUploading(false);
    }
  };

  // ── Send a message ────────────────────────────────────────────
  const submit = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;

    // Append the image reference to the text when one is pending
    const fullText = pendingImage
      ? `${trimmed}\n[Receipt image attached: ${pendingImage.url}]`
      : trimmed;

    const userMsg: DisplayMessage = {
      id: crypto.randomUUID(),
      role: "user",
      text: fullText,
      tool_calls: [],
      imageSrc: pendingImage?.localSrc,
    };
    const assistantId = crypto.randomUUID();
    const assistantMsg: DisplayMessage = {
      id: assistantId,
      role: "assistant",
      text: "",
      tool_calls: [],
      streaming: true,
    };
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setPendingImage(null);
    setInput("");
    setStreaming(true);

    // Create a conversation on the first message of a new chat.
    let convId = activeConvId;
    if (!convId) {
      const conv = await createConvMutation.mutateAsync(trimmed.slice(0, 60));
      convId = conv.id;
      setActiveConvId(convId);
    }

    const history: ChatMessage[] = [...messages, userMsg].map((m) => ({
      role: m.role,
      content: m.text,
    }));

    try {
      const response = await fetch("/api/chat/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken() ?? ""}`,
        },
        body: JSON.stringify({
          messages: history,
          max_iterations: 4,
          conversation_id: convId,
        }),
      });

      if (!response.ok || !response.body) {
        // Carries the status and the server's support reference (FIX-LOG 93).
        throw await streamHttpError(response);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

        let sepIdx: number;
        while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          const evt = parseSseEvent(raw);
          if (evt) {
            handleStreamEvent(assistantId, evt, setMessages);
            // The agent asked to open a screen — drive the app there.
            applyAgentNavigation(router, evt);
            // When done, refresh the sidebar so updated_at bubbles the
            // conversation to the top.
            if (evt.event === "done") {
              qc.invalidateQueries({ queryKey: chatKeys.conversations() });
              // Also refresh memories in case the AI called save_memory
              qc.invalidateQueries({ queryKey: chatKeys.memories() });
            }
          }
        }
      }
    } catch (err) {
      const detail = (err as Error).message;
      const friendly = withSupportRef(friendlyChatError(detail), err, { surface: "chat" });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                streaming: false,
                errored: true,
                text: m.text || friendly,
              }
            : m,
        ),
      );
    } finally {
      setStreaming(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  };

  const writeToolCount = toolsQuery.data?.filter((t) => t.is_write).length ?? 0;

  return (
    <div className="flex h-[calc(100vh-56px)] bg-slate-50 dark:bg-gray-950">
      {/* Left sidebar */}
      <ChatSidebar
        activeId={activeConvId}
        onSelect={loadConversation}
        onNew={startNewChat}
      />

      {/* Main chat area */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Chat</h1>
            <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
              {toolsQuery.data
                ? `Bynaus has ${toolsQuery.data.length} tools (${writeToolCount} write, ${
                    toolsQuery.data.length - writeToolCount
                  } read).`
                : "Loading tools…"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setMemoryOpen((v) => !v)}
            title="Memory"
            className={`text-sm rounded-md px-3 py-1.5 border transition-colors ${
              memoryOpen
                ? "bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-950/30 dark:border-purple-700 dark:text-purple-300"
                : "bg-white border-slate-200 text-slate-500 hover:border-slate-300 dark:bg-gray-900 dark:border-gray-700 dark:text-slate-400"
            }`}
          >
            🧠 Memory
          </button>
        </div>

        {/* Conversation — drag-drop images anywhere on this panel */}
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-auto px-6 py-4"
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files?.[0];
            if (file && file.type.startsWith("image/")) void uploadImage(file);
          }}
        >
          <div className="max-w-5xl mx-auto space-y-4">
            {messages.length === 0 && (
              <EmptyState
                onPick={(p) => submit(p)}
                tools={toolsQuery.data ?? []}
              />
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} />
            ))}
          </div>
        </div>

        {/* Composer */}
        <div className="bg-white border-t border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
          <div className="max-w-5xl mx-auto">
            {/* Pending image preview */}
            {pendingImage && (
              <div className="mb-2 flex items-center gap-2">
                <img
                  src={pendingImage.localSrc}
                  alt={pendingImage.filename}
                  className="max-h-20 rounded-md border border-slate-200 dark:border-gray-700 object-cover"
                />
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs text-slate-600 dark:text-slate-400 truncate max-w-[200px]">{pendingImage.filename}</span>
                  <button
                    type="button"
                    onClick={() => setPendingImage(null)}
                    className="text-[10px] text-red-500 hover:text-red-700 text-left"
                  >
                    ✕ Remove
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-end gap-2">
              {/* Camera / attach button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={streaming || imageUploading}
                title="Attach deposit slip image"
                className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 disabled:opacity-40 self-stretch flex items-center px-1"
              >
                {imageUploading ? (
                  <span className="animate-spin inline-block text-base">⏳</span>
                ) : (
                  <span className="text-lg">📎</span>
                )}
              </button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                onPaste={(e) => {
                  const items = e.clipboardData?.items;
                  if (!items) return;
                  for (let i = 0; i < items.length; i++) {
                    if (items[i].type.startsWith("image/")) {
                      const file = items[i].getAsFile();
                      if (file) {
                        e.preventDefault();
                        void uploadImage(file);
                      }
                      break;
                    }
                  }
                }}
                placeholder="Ask about your books… Paste or drop a receipt image here"
                rows={2}
                disabled={streaming}
                className="flex-1 text-sm border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none font-sans disabled:opacity-50 dark:bg-gray-800 dark:border-gray-700 dark:text-slate-100 dark:placeholder-slate-500"
              />
              <button
                type="button"
                onClick={() => submit(input)}
                disabled={!input.trim() || streaming}
                className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium disabled:opacity-50 self-stretch"
              >
                {streaming ? "Streaming…" : "Send"}
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 dark:text-slate-500">
              Paste or drop a receipt image to scan it · Write actions require accountant role
            </p>
          </div>
        </div>

        {/* Hidden file input for image attachment */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void uploadImage(f);
            e.target.value = "";
          }}
        />
      </div>

      {/* Right: memory panel */}
      {memoryOpen && (
        <MemoryPanel onClose={() => setMemoryOpen(false)} />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Error humanization
// ──────────────────────────────────────────────────────────────
function friendlyChatError(detail: string): string {
  const lower = detail.toLowerCase();
  if (lower.includes("anthropic api key")) {
    return "I can't reach the AI — no Anthropic API key is configured. Set one at /settings, then ask again.";
  }
  if (lower.includes("credit balance is too low") || lower.includes("billing")) {
    return "Bynaus is temporarily unavailable — the Anthropic API credit balance is too low. Top up at console.anthropic.com/settings/billing, then try again.";
  }
  if (lower.includes("rate limit") || lower.includes("429")) {
    return "Slow down — The AI is rate-limiting us. Try again in a moment.";
  }
  if (lower.includes("overloaded") || lower.includes("503")) {
    return "The AI is overloaded right now. Try again in a moment.";
  }
  if (lower.includes("invalid_request_error")) {
    return "The AI rejected the request. This is usually a configuration issue — check the server logs.";
  }
  const cleaned = detail.replace(/\{['"]type['"].*$/, "").trim();
  return `Sorry — that failed. ${cleaned || "Please try again."}`;
}

// ──────────────────────────────────────────────────────────────
// SSE handling
// ──────────────────────────────────────────────────────────────
interface SseEvent {
  event: string;
  data: unknown;
}

function parseSseEvent(raw: string): SseEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith(":")) continue;
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join("\n")) };
  } catch {
    return { event, data: dataLines.join("\n") };
  }
}

function handleStreamEvent(
  assistantId: string,
  evt: SseEvent,
  setMessages: React.Dispatch<React.SetStateAction<DisplayMessage[]>>,
) {
  const data = (evt.data ?? {}) as Record<string, unknown>;

  setMessages((prev) =>
    prev.map((m) => {
      if (m.id !== assistantId) return m;
      switch (evt.event) {
        case "model_selected":
          return { ...m, model: (data.model as string) ?? m.model };
        case "text_delta":
          return { ...m, text: m.text + (data.text as string ?? "") };
        case "tool_use_start": {
          const newCall: ChatToolCallTrace = {
            tool: data.tool as string,
            arguments: (data.arguments as Record<string, unknown>) ?? {},
            result: null,
            error: null,
          };
          return { ...m, tool_calls: [...m.tool_calls, newCall] };
        }
        case "tool_use_result": {
          const targetTool = data.tool as string;
          const targetArgs = JSON.stringify(data.arguments ?? {});
          const updated = m.tool_calls.map((c, idx, arr) => {
            const isMatch =
              c.tool === targetTool &&
              JSON.stringify(c.arguments) === targetArgs &&
              c.result === null &&
              c.error === null;
            if (!isMatch) return c;
            const earlierMatch = arr
              .slice(0, idx)
              .some(
                (e) =>
                  e.tool === targetTool &&
                  JSON.stringify(e.arguments) === targetArgs &&
                  e.result === null &&
                  e.error === null,
              );
            if (earlierMatch) return c;
            return {
              ...c,
              result: (data.result as Record<string, unknown>) ?? null,
              error: (data.error as string) ?? null,
            };
          });
          return { ...m, tool_calls: updated };
        }
        case "done":
          return { ...m, streaming: false };
        case "error":
          rememberStreamErrorRef(data);
          return {
            ...m,
            streaming: false,
            errored: true,
            // A turn that fails part-way keeps what was said and still shows the
            // failure and its reference (FIX-LOG 93).
            text: appendStreamError(m.text, `Sorry — ${(data.error as string) ?? "stream failed."}`),
          };
        default:
          return m;
      }
    }),
  );
}

// ──────────────────────────────────────────────────────────────
// Subcomponents
// ──────────────────────────────────────────────────────────────
function EmptyState({
  onPick,
  tools,
}: {
  onPick: (s: string) => void;
  tools: { name: string; description: string; is_write: boolean; required_role: string | null }[];
}) {
  return (
    <div className="text-center text-slate-500 py-12 dark:text-slate-400">
      <div className="text-2xl mb-2">💬</div>
      <p className="text-sm">Try one of these to see what Bynaus can answer.</p>
      <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-4xl mx-auto">
        {SUGGESTED_PROMPTS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPick(p)}
            className="text-xs bg-white border border-slate-200 hover:border-blue-400 hover:bg-blue-50 rounded-full px-3 py-1.5 text-slate-700 dark:bg-gray-900 dark:border-gray-800 dark:text-slate-300"
          >
            {p}
          </button>
        ))}
      </div>
      {tools.length > 0 && (
        <details className="mt-8 text-left max-w-3xl mx-auto bg-white border border-slate-200 rounded-md p-3 dark:bg-gray-900 dark:border-gray-800">
          <summary className="cursor-pointer text-xs font-semibold text-slate-700 dark:text-slate-300">
            Available tools ({tools.length})
          </summary>
          <ul className="mt-2 space-y-1.5">
            {tools.map((t) => (
              <li key={t.name} className="text-xs flex items-start gap-2">
                <span
                  className={`shrink-0 inline-flex items-center justify-center rounded text-[9px] font-bold uppercase px-1.5 py-0.5 ${
                    t.is_write
                      ? "bg-red-100 text-red-700"
                      : "bg-slate-100 text-slate-600"
                  }`}
                  title={t.is_write ? `Write tool · ${t.required_role}` : "Read-only"}
                >
                  {t.is_write ? "✏ write" : "👁 read"}
                </span>
                <div>
                  <code className="font-mono text-slate-900 dark:text-slate-100">{t.name}</code>
                  <span className="text-slate-500 dark:text-slate-400"> — {t.description}</span>
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function MessageBubble({ msg }: { msg: DisplayMessage }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] ${
          isUser
            ? "bg-blue-600 text-white"
            : msg.errored
            ? "bg-red-50 border border-red-200 text-red-900 dark:bg-red-950/30 dark:border-red-900 dark:text-red-200"
            : "bg-white border border-slate-200 text-slate-900 dark:bg-gray-900 dark:border-gray-800 dark:text-slate-100"
        } rounded-2xl px-4 py-3 text-sm`}
      >
        {isUser ? (
          <>
            {msg.imageSrc && (
              <img
                src={msg.imageSrc}
                alt="Attached deposit slip"
                className="max-h-32 rounded-md mb-2 object-cover"
              />
            )}
            <span className="whitespace-pre-wrap">{msg.text}</span>
          </>
        ) : msg.text ? (
          <>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                h3: ({ children }) => <p className="font-semibold mt-3 mb-1 first:mt-0">{children}</p>,
                h2: ({ children }) => <p className="font-semibold mt-3 mb-1 first:mt-0">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-0.5">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5">{children}</ol>,
                li: ({ children }) => <li className="leading-snug">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                hr: () => <hr className="my-2 border-slate-200 dark:border-gray-700" />,
                table: ({ children }) => <div className="overflow-x-auto my-2"><table className="text-xs border-collapse w-full">{children}</table></div>,
                th: ({ children }) => <th className="border border-slate-300 dark:border-gray-600 px-2 py-1 bg-slate-50 dark:bg-gray-700 text-left font-medium">{children}</th>,
                td: ({ children }) => <td className="border border-slate-300 dark:border-gray-600 px-2 py-1">{children}</td>,
                code: ({ children }) => <code className="bg-slate-100 dark:bg-gray-700 rounded px-1 text-xs font-mono">{children}</code>,
                pre: ({ children }) => <pre className="bg-slate-100 dark:bg-gray-700 rounded p-2 text-xs font-mono overflow-x-auto my-2">{children}</pre>,
              }}
            >
              {msg.text}
            </ReactMarkdown>
            {msg.streaming && (
              <span className="inline-block w-1.5 h-3.5 bg-current align-middle ml-0.5 animate-pulse" />
            )}
          </>
        ) : (
          msg.streaming
            ? <span className="italic text-slate-400 dark:text-slate-500">…</span>
            : <span className="italic text-slate-400 dark:text-slate-500">(no content)</span>
        )}
        {!isUser && msg.tool_calls.length > 0 && (
          <ToolCallList calls={msg.tool_calls} />
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Inline secret input card — posts directly to secrets API.
// The value NEVER passes through the AI or appears in chat history.
// ──────────────────────────────────────────────────────────────
interface ActionCard {
  type: "set_secret";
  key: string;
  label: string;
  hint: string;
  help: string;
  sensitive?: boolean;  // false = plain text input (URLs, env names); default true
}

// Keys that have a /test endpoint — auto-tested after save.
const TESTABLE_KEYS = new Set([
  "anthropic_api_key", "openai_api_key", "google_api_key", "mistral_api_key",
  "ollama_base_url", "taxjar_api_key", "symmetry_api_key", "bynaus_edge_url",
  "stripe_secret_key", "smtp_host", "smtp_port", "smtp_username",
  "smtp_password", "smtp_from_address", "iris_client_id", "iris_client_secret",
  "iris_environment", "tax1099_api_key", "tax1099_environment", "efile_provider",
]);

interface TestResult {
  ok: boolean;
  detail: string;
  latency_ms: number;
  error?: string | null;
}

function SecretInputCard({ card }: { card: ActionCard }) {
  const [value, setValue] = useState("");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const handleSave = async () => {
    if (!value.trim()) return;
    setSaving(true);
    setError(null);
    setTestResult(null);
    try {
      await api(`/api/admin/secrets/${card.key}`, {
        method: "PUT",
        body: { value: value.trim() },
      });
      setSaved(true);
      setValue("");
      // Auto-test if this key has a test handler
      if (TESTABLE_KEYS.has(card.key)) {
        setTesting(true);
        try {
          const result = await api<TestResult>(
            `/api/admin/secrets/${card.key}/test`,
            { method: "POST" },
          );
          setTestResult(result);
        } catch (testErr) {
          setTestResult({ ok: false, detail: "Test failed", latency_ms: 0, error: (testErr as Error).message });
        } finally {
          setTesting(false);
        }
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="mt-2 rounded-lg border border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-800 px-3 py-2.5 text-xs text-green-700 dark:text-green-300">
        <div className="flex items-center gap-2">
          <span className="text-base">✅</span>
          <span><strong>{card.label}</strong> saved.</span>
        </div>
        {testing && (
          <div className="mt-1.5 flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
            <span className="animate-spin inline-block">⏳</span>
            <span>Testing connection…</span>
          </div>
        )}
        {testResult && (
          <div className={`mt-1.5 flex items-start gap-1.5 ${testResult.ok ? "text-green-700 dark:text-green-300" : "text-red-600 dark:text-red-400"}`}>
            <span>{testResult.ok ? "✓" : "✗"}</span>
            <div>
              <span>{testResult.detail}</span>
              {testResult.error && (
                <div className="mt-0.5 text-[10px] font-mono opacity-75">{testResult.error}</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50 dark:bg-violet-950/20 dark:border-violet-800 px-3 py-3">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-sm">🔑</span>
        <span className="text-xs font-semibold text-violet-800 dark:text-violet-200">{card.label}</span>
      </div>
      <p className="text-[11px] text-violet-600 dark:text-violet-400 mb-2">{card.help}</p>
      <div className="flex gap-2">
        <input
          type={card.sensitive === false ? "text" : "password"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
          placeholder={card.hint}
          className="flex-1 text-xs border border-violet-300 dark:border-violet-700 rounded-md px-2.5 py-1.5 bg-white dark:bg-gray-900 text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 font-mono"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={!value.trim() || saving}
          className="text-xs bg-violet-600 hover:bg-violet-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50 whitespace-nowrap"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {error && (
        <p className="mt-1.5 text-[11px] text-red-600 dark:text-red-400">{error}</p>
      )}
      <p className="mt-1.5 text-[10px] text-violet-500 dark:text-violet-500">
        {card.sensitive === false
          ? "✓ Saved to settings store."
          : "🔒 Saved directly to secrets store — not visible in chat history."}
      </p>
    </div>
  );
}

function ToolCallList({ calls }: { calls: ChatToolCallTrace[] }) {
  return (
    <details className="mt-3 text-xs border-t border-slate-200 pt-2 dark:border-gray-800" open>
      <summary className="cursor-pointer text-slate-500 hover:text-slate-700 dark:text-slate-400">
        🔧 {calls.length} tool {calls.length === 1 ? "call" : "calls"}
      </summary>
      <ul className="mt-2 space-y-2">
        {calls.map((c, i) => {
          const pending = c.result === null && c.error === null;
          const actionCards = (c.result as Record<string, unknown> | null)?.action_cards as ActionCard[] | undefined;
          return (
            <li
              key={i}
              className={`rounded-md p-2 border ${
                c.error
                  ? "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900"
                  : pending
                  ? "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900"
                  : "bg-slate-50 border-slate-200 dark:bg-gray-800 dark:border-gray-700"
              }`}
            >
              <div className="font-mono text-[11px] text-slate-700 flex items-center gap-1 dark:text-slate-300">
                <span>
                  {pending ? "⏳" : c.error ? "✗" : "✓"}
                </span>
                <span>
                  {c.tool}({JSON.stringify(c.arguments)})
                </span>
              </div>
              {c.error && (
                <div className="text-red-600 text-[11px] mt-1">{c.error}</div>
              )}
              {/* Inline action cards — rendered before the raw result so they're prominent */}
              {actionCards && actionCards.length > 0 && (
                <div className="mt-2 space-y-2">
                  {actionCards.map((card) => (
                    <SecretInputCard key={card.key} card={card} />
                  ))}
                </div>
              )}
              {c.result && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[10px] text-slate-500 hover:text-slate-900 dark:text-slate-400">
                    Result
                  </summary>
                  <pre className="mt-1 text-[10px] font-mono whitespace-pre-wrap break-all max-h-48 overflow-auto bg-white border border-slate-200 rounded p-2 text-slate-800 dark:bg-gray-900 dark:border-gray-700 dark:text-slate-200">
                    {JSON.stringify(c.result, null, 2)}
                  </pre>
                </details>
              )}
            </li>
          );
        })}
      </ul>
    </details>
  );
}
